import apiClient from './client';
import type { User } from '@/types/user';

// ========== ACESSO POR DOMÍNIO (Admin / Gestor) ==========
//
// Espelha `user_domain_access` na API. O contrato é de ESTADO FINAL, não de
// diff: a tela manda a lista inteira de domain_ids e o servidor reconcilia.
// Diferente da tela de Meta Pages, que dispara uma requisição por página e
// deixa o estado no meio do caminho se uma delas falhar.

export type Papel = 'admin' | 'manager' | 'user';

export interface DomainAccess {
    user_id: number;
    role: Papel | null;
    total: number;
    domain_ids: number[];
}

export interface NovoUsuario {
    name: string;
    email: string;
    password: string;
    role?: Papel;
}

/** Domínios que um usuário enxerga. Para admin, devolve todos. */
export const getUserDomainAccess = async (userId: number): Promise<DomainAccess> => {
    const response = await apiClient.get<DomainAccess>(`/users/${userId}/domains`);
    return response.data;
};

/**
 * Define o conjunto de domínios de um gestor.
 * Quem receber domínio e ainda estiver como 'user' vira 'manager' na API.
 */
export const setUserDomainAccess = async (
    userId: number,
    domainIds: number[],
): Promise<DomainAccess> => {
    const response = await apiClient.put<DomainAccess>(`/users/${userId}/domains`, {
        domain_ids: domainIds,
    });
    return response.data;
};

/** Altera o papel de um usuário: admin, manager ou user. */
export const setUserRole = async (
    userId: number,
    role: Papel,
): Promise<{ user_id: number; role: string; success: boolean }> => {
    const response = await apiClient.patch(`/users/${userId}/role`, { role });
    return response.data;
};

/**
 * Cria um usuário já ativo e com papel definido.
 * Rota de admin — o cadastro público (`POST /register`) cria sempre inativo
 * e como 'user', ignorando qualquer papel enviado.
 */
export const criarUsuario = async (dados: NovoUsuario): Promise<{ user: User }> => {
    const response = await apiClient.post<{ user: User }>('/auth/register', dados);
    return response.data;
};

/** Ativa um usuário pendente de aprovação. */
export const aprovarUsuario = async (userId: number) => {
    const response = await apiClient.patch(`/users/${userId}/approve`);
    return response.data;
};

/** Desativa um usuário. */
export const desativarUsuario = async (userId: number) => {
    const response = await apiClient.patch(`/users/${userId}/reject`);
    return response.data;
};

/** Remove um usuário permanentemente. */
export const removerUsuario = async (userId: number) => {
    const response = await apiClient.delete(`/users/${userId}`);
    return response.data;
};
